// webviewMediaPermission.js
'use strict';
import { getIsOnWebviewApp } from '../resource/getUserAgent.js';

const WEBVIEW_PERMISSION_STATUS = {
  GRANTED: 'granted',
  DENIED: 'denied',
  PROMPT: 'prompt',
};

// Keys follow LOCAL_TRACK_TYPE values in agoraLocalTracks.js (not imported to
// avoid a circular dependency). `requestCameraPermission` is the existing
// native handler (MessageInput uses it); the microphone handler name is
// pending app-team confirmation — a missing handler falls back to PROMPT.
const BRIDGE_HANDLER_BY_TRACK_TYPE = {
  video: 'requestCameraPermission',
  audio: 'requestMicrophonePermission',
};

/**
 * Ask the native app (webview JS bridge) for a media-device permission before
 * calling getUserMedia. Android WebView cannot show the system permission
 * dialog from web code — without native cooperation the request is silently
 * ignored and getUserMedia never settles.
 * @param {object} params
 * @param {'audio'|'video'} params.trackType - Media device to ask for.
 * @returns {Promise<string>} One of WEBVIEW_PERMISSION_STATUS values.
 * @example
 * const status = await requestWebviewMediaPermission({ trackType: 'video' });
 * // => 'granted' | 'denied' | 'prompt'
 */
const requestWebviewMediaPermission = async ({ trackType } = {}) => {
  // Outside the webview app the browser shows the permission prompt itself.
  if (!getIsOnWebviewApp()) return WEBVIEW_PERMISSION_STATUS.GRANTED;
  try {
    return (
      (await window.WebApp?.callHandler?.(
        BRIDGE_HANDLER_BY_TRACK_TYPE[trackType]
      )) ?? WEBVIEW_PERMISSION_STATUS.PROMPT
    );
  } catch (_) {
    // APK without the handler: same behavior as before this bridge existed —
    // proceed to getUserMedia.
    return WEBVIEW_PERMISSION_STATUS.PROMPT;
  }
};

export { WEBVIEW_PERMISSION_STATUS, requestWebviewMediaPermission };
